import { Controller } from "@hotwired/stimulus"
import { observeSentinel, disconnectObserver, scrollToTop } from "../shared/infinite_scroll"

export default class extends Controller {
  static targets = ["results", "list", "sentinel", "spinner", "form", "usernameInput", "usernameResults", "usernameChips", "contentName", "eventTypeCheckbox", "eventTypeButton"]
  static values = { url: String, searchUsersUrl: String, initialUsers: Array }

  connect() {
    this.loading = false
    this.debounceTimer = null
    this.userSearchTimer = null
    this.selectedUsers = []
    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    this.initializeUserChips()
    this.updateEventTypeButton()
    this.observeSentinel()
    this.restoreFilters()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    clearTimeout(this.userSearchTimer)
    document.removeEventListener("click", this.boundClickOutside)
    this.disconnectObserver()
  }

  debounceFilter() {
    clearTimeout(this.debounceTimer)
    const query = this.contentNameTarget.value.trim()
    if (query.length === 1) return
    this.debounceTimer = setTimeout(() => this.filter(), 500)
  }

  filter() {
    this.updateEventTypeButton()
    this.saveFilters()
    this.fetchResults()
  }

  saveFilters() {
    const data = {}
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      for (const [key, value] of formData.entries()) {
        if (Object.prototype.hasOwnProperty.call(data, key)) {
          if (!Array.isArray(data[key])) data[key] = [data[key]]
          data[key].push(value)
        } else {
          data[key] = value
        }
      }
    }
    data.selectedUsers = this.selectedUsers
    localStorage.setItem('bingeflow_flow_filters', JSON.stringify(data))
  }

  restoreFilters() {
    const saved = localStorage.getItem('bingeflow_flow_filters')
    if (!saved) return
    const data = JSON.parse(saved)
    if (this.hasFormTarget) {
      const form = this.formTarget
      const contentName = form.querySelector('[name="content_name"]')
      if (contentName && data.content_name) contentName.value = data.content_name
      const contentType = form.querySelector('[name="content_type"]')
      if (contentType && data.content_type) contentType.value = data.content_type
      const category = form.querySelector('[name="category"]')
      if (category && data.category) category.value = data.category
      const eventTypeIds = [].concat(data['event_type_ids[]'] || [])
      this.eventTypeCheckboxTargets.forEach(cb => { cb.checked = eventTypeIds.includes(cb.value) })
    }
    if (data.selectedUsers && data.selectedUsers.length > 0) {
      data.selectedUsers.forEach(user => {
        this.selectedUsers.push(user)
        const chip = document.createElement('span')
        chip.className = 'flow-username-chip'
        chip.dataset.userId = user.id
        chip.innerHTML = `${user.username}<button type="button" data-action="click->flow#removeUser" data-user-id="${user.id}">&times;</button>`
        this.usernameChipsTarget.appendChild(chip)
      })
      this.usernameInputTarget.placeholder = ''
    }
    this.updateEventTypeButton()
    this.fetchResults()
  }

  updateEventTypeButton() {
    if (!this.hasEventTypeCheckboxTarget || !this.hasEventTypeButtonTarget) return
    const checked = this.eventTypeCheckboxTargets.filter(cb => cb.checked).length
    this.eventTypeButtonTarget.textContent = checked > 0 ? `Event Types (${checked})` : "Event Types"
  }

  fetchResults() {
    const params = this.buildFilterParams()
    const url = this.urlValue + "?" + params.toString()

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(response => response.text())
      .then(html => {
        this.disconnectObserver()
        this.resultsTarget.innerHTML = html
        this.observeSentinel()
      })
  }

  initializeUserChips() {
    if (!this.hasInitialUsersValue) return

    this.initialUsersValue.forEach(user => {
      this.selectedUsers.push(user)
      const chip = document.createElement("span")
      chip.className = "flow-username-chip"
      chip.dataset.userId = user.id
      chip.innerHTML = `${user.username}<button type="button" data-action="click->flow#removeUser" data-user-id="${user.id}">&times;</button>`
      this.usernameChipsTarget.appendChild(chip)
    })

    if (this.selectedUsers.length > 0) {
      this.usernameInputTarget.placeholder = ""
    }
  }

  buildFilterParams() {
    const params = new URLSearchParams()
    if (this.hasFormTarget) {
      const formData = new FormData(this.formTarget)
      for (const [key, value] of formData.entries()) {
        params.append(key, value)
      }
    }
    this.selectedUsers.forEach(user => {
      params.append("user_ids[]", user.id)
    })
    return params
  }

  loadMore() {
    if (this.loading || !this.hasSentinelTarget) return

    const nextPage = this.sentinelTarget.dataset.nextPage
    if (!nextPage) return

    this.loading = true
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("d-none")

    const params = this.buildFilterParams()
    params.set("page", nextPage)
    const url = this.urlValue + "?" + params.toString()

    fetch(url, {
      headers: { "X-Requested-With": "XMLHttpRequest" }
    })
      .then(response => response.text())
      .then(html => {
        this.disconnectObserver()

        if (this.hasSpinnerTarget) this.spinnerTarget.remove()
        if (this.hasSentinelTarget) this.sentinelTarget.remove()

        const temp = document.createElement("div")
        temp.innerHTML = html

        const cards = temp.querySelectorAll(".flow-event")
        cards.forEach(card => this.listTarget.appendChild(card))

        const match = html.match(/<!-- next_page:(\d+) -->/)
        if (match) {
          const sentinel = document.createElement("div")
          sentinel.dataset.flowTarget = "sentinel"
          sentinel.dataset.nextPage = match[1]
          this.resultsTarget.appendChild(sentinel)

          const spinner = document.createElement("div")
          spinner.className = "flow-loading text-center py-4"
          spinner.dataset.flowTarget = "spinner"
          spinner.innerHTML = '<div class="spinner-border" role="status"><span class="visually-hidden">Loading...</span></div>'
          this.resultsTarget.insertBefore(spinner, sentinel)

          this.observeSentinel()
        }

        this.loading = false
      })
  }

  debounceUserSearch() {
    clearTimeout(this.userSearchTimer)
    this.userSearchTimer = setTimeout(() => this.performUserSearch(), 300)
  }

  onUserFocus() {
    const query = this.usernameInputTarget.value.trim()
    if (query.length >= 2) this.performUserSearch()
  }

  performUserSearch() {
    const query = this.usernameInputTarget.value.trim()
    if (query.length < 2) {
      this.usernameResultsTarget.classList.add("d-none")
      this.usernameResultsTarget.innerHTML = ""
      return
    }

    fetch(this.searchUsersUrlValue + "?q=" + encodeURIComponent(query))
      .then(response => response.json())
      .then(data => {
        this.usernameResultsTarget.innerHTML = ""
        const filtered = data.users.filter(u => !this.selectedUsers.some(s => s.id === u.id))

        if (filtered.length === 0) {
          this.usernameResultsTarget.innerHTML = '<div class="flow-username-empty">No users found</div>'
        } else {
          filtered.forEach(user => {
            const item = document.createElement("div")
            item.className = "flow-username-item"

            let imageHtml
            if (user.image) {
              imageHtml = `<img src="${user.image}" alt="${user.username}" class="flow-user-image">`
            } else {
              imageHtml = `<div class="flow-user-placeholder">${user.username.charAt(0).toUpperCase()}</div>`
            }

            item.innerHTML = `<div class="d-flex align-items-center gap-2">${imageHtml}<span>${user.username}</span></div>`
            item.addEventListener("click", () => this.selectUser(user))
            this.usernameResultsTarget.appendChild(item)
          })
        }

        this.usernameResultsTarget.classList.remove("d-none")
      })
  }

  focusUsernameInput() {
    this.usernameInputTarget.focus()
  }

  selectUser(user) {
    if (this.selectedUsers.some(u => u.id === user.id)) return

    this.selectedUsers.push(user)

    const chip = document.createElement("span")
    chip.className = "flow-username-chip"
    chip.dataset.userId = user.id
    chip.innerHTML = `${user.username}<button type="button" data-action="click->flow#removeUser" data-user-id="${user.id}">&times;</button>`
    this.usernameChipsTarget.appendChild(chip)

    this.usernameInputTarget.value = ""
    this.usernameInputTarget.placeholder = ""
    this.usernameResultsTarget.classList.add("d-none")
    this.usernameResultsTarget.innerHTML = ""

    this.filter()
  }

  removeUser(event) {
    const userId = parseInt(event.currentTarget.dataset.userId)
    this.selectedUsers = this.selectedUsers.filter(u => u.id !== userId)

    const chip = this.usernameChipsTarget.querySelector(`[data-user-id="${userId}"]`)
    if (chip) chip.remove()

    if (this.selectedUsers.length === 0) {
      this.usernameInputTarget.placeholder = "Search users..."
    }

    this.filter()
  }

  scrollToTop(event) {
    scrollToTop(event)
  }

  clickOutside(event) {
    if (this.hasUsernameInputTarget && this.hasUsernameResultsTarget) {
      if (!this.usernameInputTarget.contains(event.target) && !this.usernameResultsTarget.contains(event.target)) {
        this.usernameResultsTarget.classList.add("d-none")
      }
    }
  }

  observeSentinel() {
    observeSentinel(this, { rootMargin: "200px" })
  }

  disconnectObserver() {
    disconnectObserver(this)
  }
}
